import React from 'react';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog.tsx';

export type DialogVariant = 'destructive' | 'info' | 'neutral';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: DialogVariant;
  isLoading?: boolean;
  loadingText?: string;
  onConfirm: () => void;
  onCancel?: () => void;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmText,
  cancelText = 'Anuluj',
  variant = 'neutral',
  isLoading = false,
  loadingText,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    onOpenChange(false);
  };

  const getDefaultConfirmText = (variant: DialogVariant): string => {
    switch (variant) {
      case 'destructive':
        return 'Usuń';
      case 'info':
        return 'OK';
      case 'neutral':
      default:
        return 'Potwierdź';
    }
  };

  const showCancelButton = variant !== 'info';
  const finalConfirmText =
    isLoading && loadingText
      ? loadingText
      : confirmText || getDefaultConfirmText(variant);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogDescription>{description}</DialogDescription>
        <DialogFooter>
          {showCancelButton && (
            <ActionButton
              tone="outline"
              size="sm"
              onClick={handleCancel}
              disabled={isLoading}
            >
              {cancelText}
            </ActionButton>
          )}
          <ActionButton
            onClick={onConfirm}
            isLoading={isLoading}
            size="sm"
            tone={variant === 'destructive' ? 'danger' : 'accent'}
          >
            {finalConfirmText}
          </ActionButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
